import { use, type CSSProperties } from "react";

import { SettingsContext } from "lib/settings/use-settings";
import type { VirtualLayout } from "lib/types";
import { useViewportWidth } from "lib/utils/viewport";

import { ContentLayoutContext } from "./use-content-layout";

function getColumnGap(margins: number): number {
  return margins * 2.5;
}

function getColumnWidth(
  viewportWidth: number,
  margins: number,
  cols: number
): number {
  const columnCount = cols || 1;
  return viewportWidth / columnCount - getColumnGap(margins);
}

function snapUpToPage(value: number, pageWidth: number): number {
  return pageWidth > 0 ? Math.ceil(value / pageWidth) * pageWidth : value;
}

interface ContentStyles {
  root: CSSProperties;
  content: CSSProperties;
}

export function useContentStyles(
  layoutOverride?: VirtualLayout | null
): ContentStyles {
  const { layout: contextLayout } = use(ContentLayoutContext)!;
  const layout = layoutOverride === undefined ? contextLayout : layoutOverride;
  const viewportWidth = useViewportWidth();

  const {
    layoutDirection,
    cols,
    margins,
    font,
    fontSize,
    lineHeight,
    textAlignJustify,
    wordWrap,
  } = use(SettingsContext)!;

  const columnGap = getColumnGap(margins);
  const pagePadding = columnGap / 2;
  const columnWidthPx = getColumnWidth(viewportWidth, margins, cols);

  // Иначе CSS auto-balance растягивает колонки шире declared column-width
  const snappedContentWidth =
    layout?.mode === "virtual"
      ? snapUpToPage(layout.contentWidth, viewportWidth) - columnGap
      : null;

  return {
    root: {
      ["--column-gap" as keyof CSSProperties]: `${columnGap}px`,
      ["--content-padding" as keyof CSSProperties]: `${pagePadding}px`,
      paddingLeft: `${pagePadding}px`,
      paddingRight: `${pagePadding}px`,
    },
    content: {
      columnWidth:
        layoutDirection === "horizontal" ? `${columnWidthPx}px` : undefined,
      width: snappedContentWidth !== null ? `${snappedContentWidth}px` : "auto",
      fontFamily: font,
      fontSize: fontSize ? `${fontSize}px` : undefined,
      lineHeight,
      textAlign: textAlignJustify ? ("justify" as const) : ("left" as const),
      hyphens: wordWrap ? ("none" as const) : ("manual" as const),
      WebkitHyphens: wordWrap ? ("none" as const) : ("manual" as const),
    },
  };
}
